import {
  useState,
  useEffect,
  Fragment,
  type FC,
} from 'react';
import Cookies from 'js-cookie';
import CookieConsent, {
  getCookieConsentValue
} from 'react-cookie-consent';
import {
  Box,
  Stack,
  Typography
} from '@mui/joy';
import {
  cookieSettingsIcon,
} from '../assets';
import {
  ENVIRONMENT,
  COOKIE_NAME_USAGE_CONSENT,
  COOKIE_CONSENT_EXPIRES_DAYS,
} from '../config';
import {
  OverriddenCookieConsentStyles
} from './overridden-cookie-consent-styles.component';

export type ConfirmCookieConsentBannerProps = {
  onAccepted?: () => void;
  onDeclined?: () => void;
};

// TODO: extract to separate feature or move to entities
export const ConfirmCookieConsentBanner: FC<ConfirmCookieConsentBannerProps> = ({
  onAccepted,
  onDeclined
}) => {
  const [showBanner, setShowBanner] = useState<boolean>(false);

  useEffect(() => {
    const consent = getCookieConsentValue(COOKIE_NAME_USAGE_CONSENT);
    if (consent === undefined) setShowBanner(true);
    else {
      if (consent === 'true') onAccepted?.();
      else if (consent === 'false') onDeclined?.();
    }
  }, [onAccepted, onDeclined]);

  const handleAccept = (): void => {
    Cookies.set(COOKIE_NAME_USAGE_CONSENT, 'true', {
      expires: COOKIE_CONSENT_EXPIRES_DAYS,
      path: '/',
    });
    onAccepted?.();
  };

  const handleDecline = (): void => {
    Cookies.set(COOKIE_NAME_USAGE_CONSENT, 'false', {
      expires: COOKIE_CONSENT_EXPIRES_DAYS,
      path: '/',
    });
    onDeclined?.();
  };

  // TODO: move svg to assets
  return showBanner && (
    <Fragment>
      <CookieConsent
        location='bottom'
        enableDeclineButton
        visible='byCookieValue'
        buttonText='Accept All'
        declineButtonText='Decline'
        debug={ENVIRONMENT === 'development'}
        expires={COOKIE_CONSENT_EXPIRES_DAYS}
        cookieName={COOKIE_NAME_USAGE_CONSENT}
        buttonClasses='cookie-consent-button'
        contentClasses='cookie-consent-content'
        containerClasses='cookie-consent-container'
        declineButtonClasses='cookie-consent-decline-button'
        buttonWrapperClasses='cookie-consent-button-wrapper'
        onAccept={handleAccept}
        onDecline={handleDecline}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='189'
          height='80'
          viewBox='0 0 189 80'
          fill='none'
          style={{
            zIndex: -1,
            top: 0,
            left: 0,
            height: '100%',
            position: 'absolute',
          }}>
          <g style={{ mixBlendMode: 'plus-lighter' }} opacity='0.06' filter='url(#filter0_f_297_31976)'>
            <ellipse cx='61.1212' cy='72.7608' rx='61.1212' ry='72.7608'
              transform='matrix(-0.709334 -0.704872 -0.704872 0.709334 122.285 25.1653)' fill='#00BBFF' />
          </g>
          <defs>
            <filter id='filter0_f_297_31976' x='-133.516' y='-127.537' width='322.317' height='322.463'
              filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'>
              <feFlood floodOpacity='0' result='BackgroundImageFix' />
              <feBlend mode='normal' in='SourceGraphic' in2='BackgroundImageFix' result='shape' />
              <feGaussianBlur stdDeviation='47' result='effect1_foregroundBlur_297_31976' />
            </filter>
          </defs>
        </svg>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={({ breakpoints }) => ({
            gap: 2,
            [breakpoints.down(768)]: {
              flexDirection: 'column',
            }
          })}>
          <Box
            component='img'
            src={cookieSettingsIcon.default}
            sx={{ width: 48, height: 48 }}
          />
          <Typography
            sx={({ breakpoints }) => ({
              fontWeight: 500,
              fontSize: 12,
              lineHeight: '20px',
              letterSpacing: '0%',
              verticalAlign: 'middle',
              [breakpoints.down(768)]: {
                textAlign: 'center',
              }
            })}>
            This site uses cookies to improve your experience and to analyze how our website is used and performs.
            We may also share information about your usage with our partners in social media, advertising, and analytics.&nbsp;&nbsp;
            <Box
              component='a'
              href='/privacy-policy'
              sx={{
                color: '#00BBFF',
                textDecoration: 'none',
              }}>
              More About Cookie Notice
            </Box>
          </Typography>
        </Stack>
        <svg xmlns='http://www.w3.org/2000/svg' width='167' height='80' viewBox='0 0 167 80' fill='none'
          style={{
            zIndex: -1,
            top: 0,
            right: 0,
            height: '100%',
            position: 'absolute',
          }}>
          <g style={{ mixBlendMode: 'plus-lighter' }} opacity='0.06' filter='url(#filter0_f_297_31977)'>
            <ellipse cx='161.642' cy='33.6943' rx='61.1212' ry='72.7608'
              transform='rotate(-44.8192 161.642 33.6943)' fill='#00BBFF' />
          </g>
          <defs>
            <filter id='filter0_f_297_31977' x='0.484131' y='-127.537' width='322.317' height='322.463'
              filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'>
              <feFlood floodOpacity='0' result='BackgroundImageFix' />
              <feBlend mode='normal' in='SourceGraphic' in2='BackgroundImageFix' result='shape' />
              <feGaussianBlur stdDeviation='47' result='effect1_foregroundBlur_297_31977' />
            </filter>
          </defs>
        </svg>
      </CookieConsent>
      <OverriddenCookieConsentStyles />
    </Fragment>
  );
};